import React, { useState } from 'react';
import { UserProfile } from '../../types';
import { Sparkles, Save, MessageSquare, ShieldAlert } from 'lucide-react';
import { soundEffects } from '../../lib/soundEffects';

interface Props {
  currentUser: UserProfile;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
}

export const AISettingsSection: React.FC<Props> = ({ currentUser, onUpdateProfile }) => {
  const [ai, setAi] = useState(currentUser.aiSettings || {
    enabled: true,
    shareContext: false,
    smartReplies: true,
    autoTranslate: true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearedSuccess, setClearedSuccess] = useState(false);

  const handleSave = async () => {
    soundEffects.playTap();
    setIsSaving(true);
    await onUpdateProfile({ aiSettings: ai });
    setIsSaving(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const handleClearHistory = () => {
    soundEffects.playTap();
    setIsClearing(true);
    setTimeout(() => {
      setIsClearing(false);
      setClearedSuccess(true);
      setTimeout(() => setClearedSuccess(false), 2000);
    }, 1500); // Simulate clearing history
  };

  const updateSettings = (key: keyof typeof ai, val: any) => {
    setAi({ ...ai, [key]: val });
  };

  const renderToggle = (label: string, key: keyof typeof ai, description?: string) => (
    <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800">
      <div>
        <div className="text-sm font-semibold text-slate-200">{label}</div>
        {description && <div className="text-[10px] text-slate-400 mt-1 max-w-[200px] leading-relaxed">{description}</div>}
      </div>
      <button
        onClick={() => updateSettings(key, !ai[key])}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${ai[key] ? 'bg-fuchsia-500' : 'bg-slate-700'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${ai[key] ? 'translate-x-1' : '-translate-x-6'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#0e1422] rounded-3xl p-5 border border-fuchsia-500/30 shadow-[0_0_20px_rgba(217,70,239,0.05)]">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-fuchsia-400" />
          وتين الذكي (AI)
        </h3>

        <div className="space-y-3">
          {renderToggle('تفعيل وتين الذكي', 'enabled', 'السماح للمساعد الذكي بالعمل داخل المحادثات لمساعدتك في كتابة الردود وتلخيصها.')}
          {renderToggle('الردود الذكية', 'smartReplies', 'اقتراح ردود ذكية وسريعة بناءً على سياق الرسائل الأخيرة.')}
          {renderToggle('الترجمة الذكية الفورية', 'autoTranslate', 'ترجمة الرسائل الأجنبية إلى لغتك تلقائياً باستخدام تقنيات AI.')}
          
          <div className="h-px w-full bg-slate-800/80 my-2"></div>

          <div className="flex items-start gap-3 p-3 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-xl">
            <ShieldAlert className="w-5 h-5 text-fuchsia-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-fuchsia-100">خصوصية البيانات</div>
              <div className="text-xs text-fuchsia-300/80 mt-1 leading-relaxed">
                لا يتم إرسال محادثاتك الخاصة إلى سيرفرات الذكاء الاصطناعي بشكل تلقائي. يتم فقط إرسال السياق عند طلبك المباشر أو عند تفعيل الاقتراحات الذكية (ويمكنك إيقافها).
              </div>
            </div>
          </div>
          
          {renderToggle('مشاركة سياق المحادثة المحدود', 'shareContext', 'السماح لـ AI برؤية آخر 3 رسائل فقط عند طلب تلخيص أو مساعدة لفهم السياق. (نوصي بتفعيله)')}

          <button onClick={handleClearHistory} disabled={isClearing} className={`w-full mt-2 py-3 border border-slate-700/80 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${clearedSuccess ? 'bg-fuchsia-500/10 text-fuchsia-400' : 'bg-[#141c2a] text-rose-400 hover:bg-rose-500/10 disabled:opacity-50'}`}>
            <MessageSquare className="w-4 h-4" />
            {isClearing ? 'جاري المسح...' : clearedSuccess ? 'تم مسح السجل بنجاح' : 'مسح سجل محادثاتي مع وتين الذكي'}
          </button>
        </div>

        <button disabled={isSaving} onClick={handleSave} className={`mt-4 w-full py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 shadow-lg shadow-fuchsia-500/20 ${savedSuccess ? 'bg-fuchsia-400 text-slate-900' : 'bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white disabled:opacity-50'}`}>
          <Save className="w-4 h-4" />
          {isSaving ? 'جاري الحفظ...' : savedSuccess ? 'تم الحفظ' : 'حفظ إعدادات الذكاء الاصطناعي'}
        </button>
      </div>
    </div>
  );
};
